import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ShopTimeDialog({ open, onOpenChange, onSaved, defaultDate, editing }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [hours, setHours] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/settings");
      const cats = data?.shop_time_categories || [];
      setCategories(cats);
      if (editing) {
        setCategory(editing.category || cats[0] || "");
        setHours(editing.hours ?? "");
        setNotes(editing.notes || "");
      } else {
        setCategory(cats[0] || "");
        setHours("");
        setNotes("");
      }
    })();
  }, [open, editing]);

  const save = async () => {
    if (!category) { toast.error("Pick a category"); return; }
    const h = Number(hours);
    if (!h || h <= 0) { toast.error("Hours required and must be > 0"); return; }
    setBusy(true);
    try {
      const payload = { category, hours: h, notes };
      if (defaultDate && !editing) payload.date = defaultDate;
      if (editing) await api.put(`/shop-time/${editing.id}`, payload);
      else await api.post("/shop-time", payload);
      toast.success(editing ? "Shop time updated — pending re-approval" : "Shop time submitted for admin approval");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit Shop / Other Time" : "Add Shop / Other Time"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="shop-time-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {categories.length === 0 && <SelectItem value="-" disabled>No categories configured</SelectItem>}
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Hours <span className="text-red-500">*</span></Label>
            <Input
              type="number"
              step="0.25"
              min="0.25"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g., 2.0"
              className="mt-1"
              data-testid="shop-time-hours-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" placeholder="What did you work on?" data-testid="shop-time-notes-input" />
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            This entry requires admin approval. Hours are paid at your hourly rate and added to your day's pay once approved. You can still finalize your day — pending entries roll into your pay after approval.
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={busy} className="flex-1 bg-sky-600 hover:bg-sky-700" data-testid="shop-time-save-btn">
              {busy ? "Saving…" : (editing ? "Save" : "Submit for approval")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
